import React, { useState } from 'react';
import { MessageCircle, Copy, Check, RefreshCw, ChevronDown, ChevronUp, Key } from 'lucide-react';
import { DialogShell } from './ui/DialogShell';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';
import { Usuario } from '../types/auth';
import { generateSecurePassword } from '../utils/formatters';
import { usuarioService } from '../services/usuarioService';
import { FormErrorAlert } from './ui/FormErrorAlert';

interface ContrasenaGeneradaProps {
  readonly password: string;
  readonly activa: boolean;
  readonly onRegenerar: () => void;
  readonly onCopiar: () => void;
  readonly copiado: boolean;
  readonly deshabilitado: boolean;
  readonly avisoInactiva: string;
}

const ContrasenaGenerada: React.FC<ContrasenaGeneradaProps> = ({
  password,
  activa,
  onRegenerar,
  onCopiar,
  copiado,
  deshabilitado,
  avisoInactiva
}) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="block text-sm font-medium text-slate-700">
        {activa ? 'Contraseña Activa' : 'Contraseña Generada'}
      </label>
      {!activa && (
        <button
          type="button"
          onClick={onRegenerar}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          disabled={deshabilitado}
        >
          <RefreshCw className="w-4 h-4" />
          Regenerar
        </button>
      )}
    </div>
    <div className="flex gap-2">
      <div className="flex-1 p-3 bg-slate-100 border border-slate-300 rounded-lg font-mono text-sm">
        {password}
      </div>
      <Button
        type="button"
        variant="secondary"
        onClick={onCopiar}
        disabled={deshabilitado || !activa}
        className="flex items-center gap-2"
      >
        {copiado ? (
          <>
            <Check className="w-4 h-4" />
            Copiada
          </>
        ) : (
          <>
            <Copy className="w-4 h-4" />
            Copiar
          </>
        )}
      </Button>
    </div>
    {!activa && (
      <p className="mt-2 text-xs text-slate-500">{avisoInactiva}</p>
    )}
  </div>
);

interface CompartirPorWhatsAppProps {
  readonly mensaje: string;
  readonly filas: number;
  readonly expandido: boolean;
  readonly onToggle: () => void;
  readonly onCopiar: () => void;
  readonly copiado: boolean;
  readonly copiaHabilitada: boolean;
  readonly deshabilitado: boolean;
}

const CompartirPorWhatsApp: React.FC<CompartirPorWhatsAppProps> = ({
  mensaje,
  filas,
  expandido,
  onToggle,
  onCopiar,
  copiado,
  copiaHabilitada,
  deshabilitado
}) => {
  const toggleLabel = expandido ? 'Ocultar vista previa del mensaje' : 'Ver vista previa del mensaje';

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 p-4 bg-slate-50">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
          disabled={deshabilitado}
          aria-expanded={expandido}
          aria-label={toggleLabel}
        >
          <MessageCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="font-medium text-slate-700 truncate">Compartir por WhatsApp</span>
        </button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCopiar}
          disabled={!copiaHabilitada}
          className="flex items-center gap-2"
        >
          {copiado ? (
            <>
              <Check className="w-4 h-4" />
              Mensaje Copiado
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copiar Mensaje
            </>
          )}
        </Button>
        <button
          type="button"
          onClick={onToggle}
          className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200 transition-colors flex-shrink-0"
          disabled={deshabilitado}
          aria-expanded={expandido}
          aria-label={toggleLabel}
        >
          {expandido ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </button>
      </div>

      {expandido && (
        <div className="p-4 bg-white border-t border-slate-200">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Mensaje Completo
          </label>
          <textarea
            value={mensaje}
            readOnly
            rows={filas}
            className="w-full p-3 border border-slate-300 rounded-lg bg-slate-50 text-sm resize-none"
          />
        </div>
      )}
    </div>
  );
};

interface EnviarCredencialesModalProps {
  readonly usuario: Usuario;
  readonly authInfo: { hasLoggedIn: boolean; lastSignIn: string | null; hasCredentials: boolean };
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export const EnviarCredencialesModal: React.FC<EnviarCredencialesModalProps> = ({
  usuario,
  authInfo,
  onClose,
  onSuccess
}) => {
  const { success: showSuccess, error: showError } = useToast();
  // Vacía hasta que el admin genera explícitamente: mostrar una contraseña al abrir
  // sugiere que ya está activa, y no lo está (la actual es irrecuperable, va hasheada).
  const [password, setPassword] = useState('');
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [copiedMessage, setCopiedMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showWhatsApp, setShowWhatsApp] = useState(false);

  // El miembro ya tiene cuenta creada (user_id), así que ya tiene una contraseña activa:
  // aquí se la reemplaza, no se le asigna la primera.
  const esReasignacion = authInfo.hasCredentials;
  const accionLabel = esReasignacion ? 'Restablecer contraseña' : 'Asignar contraseña';
  const generarLabel = esReasignacion ? 'Regenerar contraseña' : 'Generar contraseña';

  const getMensaje = () => {
    const primerNombre = usuario.nombres.trim().split(/\s+/)[0];
    if (!authInfo.hasCredentials) {
      // Usuario nuevo - Mensaje de bienvenida
      return `¡Hola ${primerNombre}!

Te damos la bienvenida a la Aplicación de Gestión Administrativa Gnóstica.

Tus credenciales de acceso son:

📧 Email: ${usuario.email}
🔑 Contraseña: ${password}

Por favor, ingresa a la aplicación con estos datos.

URL: ${window.location.origin}

Nos puedes preguntar lo que necesites.`;
    } else {
      // Usuario existente - Mensaje de recuperación
      return `Hola ${usuario.nombres},

Se ha generado una nueva contraseña para tu cuenta en Gestión Administrativa Gnóstica.

Tus credenciales actualizadas son:

📧 Email: ${usuario.email}
🔑 Contraseña: ${password}

⚠️ Importante: Tu contraseña anterior ya no funcionará.

Por favor, ingresa a la aplicación con estas nuevas credenciales.

URL: ${window.location.origin}

¡Saludos!`;
    }
  };

  const mensaje = getMensaje();

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiedPassword(true);
      showSuccess('Contraseña copiada al portapapeles');
      setTimeout(() => setCopiedPassword(false), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
      showError('Error al copiar la contraseña');
    }
  };

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(mensaje);
      setCopiedMessage(true);
      showSuccess('Mensaje copiado al portapapeles');
      setTimeout(() => setCopiedMessage(false), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
      showError('Error al copiar el mensaje');
    }
  };

  const handleGenerarPassword = () => {
    setPassword(generateSecurePassword(12));
    setCopiedPassword(false);
    setCopiedMessage(false);
    setSuccess(false);
  };

  const handleConfirmar = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await usuarioService.updatePassword(usuario.id, password);
      setSuccess(true);
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Error al actualizar contraseña';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (success) {
      onSuccess();
    }
    onClose();
  };

  return (
    <DialogShell isOpen={true} onClose={handleClose} title={accionLabel}>
      <div className="space-y-4">
        {success && (
          <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">
              Contraseña {esReasignacion ? 'restablecida' : 'asignada'} exitosamente. Ya está activa —
              puedes copiarla y compartirla antes de cerrar.
            </p>
          </div>
        )}

        <FormErrorAlert error={error} />

        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            {esReasignacion ? (
              <>
                Se generará una nueva contraseña para <strong>{usuario.nombres} {usuario.apellidos}</strong>.
                La contraseña anterior dejará de funcionar.
              </>
            ) : (
              <>
                Se asignará una contraseña para <strong>{usuario.nombres} {usuario.apellidos}</strong> y
                podrá acceder a la aplicación por primera vez.
              </>
            )}
          </p>
        </div>

        {password && (
          <ContrasenaGenerada
            password={password}
            activa={success}
            onRegenerar={handleGenerarPassword}
            onCopiar={handleCopyPassword}
            copiado={copiedPassword}
            deshabilitado={loading}
            avisoInactiva={`Todavía no está activa. Haz clic en "${accionLabel}" para activarla y poder copiarla.`}
          />
        )}

        {password && (
          <CompartirPorWhatsApp
            mensaje={mensaje}
            filas={esReasignacion ? 14 : 12}
            expandido={showWhatsApp}
            onToggle={() => { setShowWhatsApp(!showWhatsApp); }}
            onCopiar={handleCopyMessage}
            copiado={copiedMessage}
            copiaHabilitada={!loading && success}
            deshabilitado={loading}
          />
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {success ? 'Cerrar' : 'Cancelar'}
          </Button>
          {password ? (
            <Button
              type="button"
              variant={success ? 'success' : 'primary'}
              onClick={handleConfirmar}
              disabled={loading || success}
              isLoading={loading}
              className="flex items-center gap-2"
            >
              {success ? (
                <>
                  <Check className="w-4 h-4" />
                  {esReasignacion ? 'Restablecida' : 'Asignada'}
                </>
              ) : (
                <>
                  <Key className="w-4 h-4" />
                  {accionLabel}
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              onClick={handleGenerarPassword}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              {generarLabel}
            </Button>
          )}
        </div>

        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs text-amber-800">
            <strong>Nota:</strong> La contraseña actual del usuario no se puede consultar. Aquí se
            genera una nueva, que recién queda activa al hacer clic en "{accionLabel}" — solo entonces
            se habilitan los botones de copiar. Puedes regenerarla las veces que quieras antes de eso.
          </p>
        </div>
      </div>
    </DialogShell>
  );
};
